// =============================================================================
// navigation/navigationRef.ts — imperative navigation from ABOVE the tree.
//
// Blueprint §2 / Risk #3: some callers fire navigation from outside any screen
// (AnnouncementBanner, InAppNotification, the root incoming-call Alert, the
// push-tap deep-link router). expo-router let them call useRouter() anywhere;
// react-navigation needs a ref. This is the stable imperative API those callers
// import instead of a hook — the P2/P3 equivalent of `router.{push,replace,back}`.
// =============================================================================
import {
  createNavigationContainerRef,
  StackActions,
  CommonActions,
} from '@react-navigation/native';
import type { RootStackParamList } from './types';

export const navigationRef = createNavigationContainerRef<RootStackParamList>();

/** router.push equivalent — navigate by route name + params. */
export function navigate<Name extends keyof RootStackParamList>(
  name: Name,
  params?: RootStackParamList[Name],
): void {
  if (navigationRef.isReady()) {
    // @ts-expect-error params typing widens across the union; safe at call sites
    navigationRef.navigate(name, params);
  }
}

/** router.push that always pushes a new instance (e.g. nested same-screen). */
export function push<Name extends keyof RootStackParamList>(
  name: Name,
  params?: RootStackParamList[Name],
): void {
  if (navigationRef.isReady()) {
    navigationRef.dispatch(StackActions.push(name as string, params));
  }
}

/** router.replace equivalent. */
export function replace<Name extends keyof RootStackParamList>(
  name: Name,
  params?: RootStackParamList[Name],
): void {
  if (navigationRef.isReady()) {
    navigationRef.dispatch(StackActions.replace(name as string, params));
  }
}

/** router.back equivalent. */
export function goBack(): void {
  if (navigationRef.isReady() && navigationRef.canGoBack()) {
    navigationRef.goBack();
  }
}

/** Hard reset to a single route (the `<Redirect>` / auth-bounce equivalent). */
export function resetTo<Name extends keyof RootStackParamList>(
  name: Name,
  params?: RootStackParamList[Name],
): void {
  if (navigationRef.isReady()) {
    navigationRef.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: name as string, params }],
      }),
    );
  }
}
